import { Link, NavLink } from 'react-router-dom'
import { useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { SmartImage } from '../ui/SmartImage'

const navItems = [
  // Flagship product — leads the nav; external (opens speak.thearchivenetwork.com).
  { path: 'https://speak.thearchivenetwork.com', label: 'Speak with History' },
  { path: '/archives', label: 'Archives' },
  { path: '/podcasts', label: 'Podcasts' },
  { path: '/conversations', label: 'YouTube' },
  { path: '/curators', label: 'Curators' },
  { path: '/support', label: 'Support' },
]

export default function TopNav() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { user, loading } = useAuth()

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-archive-black/95 backdrop-blur-sm border-b border-archive-stone/50">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 group"
            aria-label="The Archive Network Home"
          >
            <div className="w-10 h-10 rounded-full overflow-hidden border border-archive-gold/30 group-hover:border-archive-gold transition-colors">
              <SmartImage
                  src="/images/archive-network-seal.png"
                  alt="The Archive Network"
                  sizes="54px"
                  width={54}
                  height={54}
                  loading="eager"
                  className="w-full h-full object-cover"
                />
            </div>
            <span className="hidden sm:block font-display text-archive-cream text-lg tracking-wide">
              The Archive Network
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {/* Search Button */}
            <NavLink
              to="/search"
              className={({ isActive }) =>
                `p-2 text-sm transition-colors duration-200 rounded ${
                  isActive
                    ? 'text-archive-gold bg-archive-gold/10'
                    : 'text-archive-text hover:text-archive-gold hover:bg-archive-stone/50'
                }`
              }
              aria-label="Search archives"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </NavLink>

            {navItems.map((item) =>
              item.path.startsWith('http') ? (
                // External flagship item — subtle gold accent to read as the product.
                <a
                  key={item.path}
                  href={item.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 text-sm font-medium tracking-wide transition-colors duration-200 rounded text-archive-gold hover:bg-archive-gold/10"
                >
                  {item.label}
                </a>
              ) : (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `px-4 py-2 text-sm font-medium tracking-wide transition-colors duration-200 rounded ${
                      isActive
                        ? 'text-archive-gold bg-archive-gold/10'
                        : 'text-archive-text hover:text-archive-gold hover:bg-archive-stone/50'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              )
            )}

            {/* Auth Button */}
            {!loading && (
              <Link
                to={user ? '/profile' : '/login'}
                className="ml-2 flex items-center gap-2 px-4 py-2 text-sm font-medium tracking-wide transition-colors duration-200 rounded border border-archive-gold/30 text-archive-gold hover:bg-archive-gold/10 hover:border-archive-gold"
              >
                {user ? (
                  <>
                    <span className="w-6 h-6 rounded-full bg-archive-gold/20 flex items-center justify-center text-xs">
                      {user.name ? user.name[0].toUpperCase() : user.email[0].toUpperCase()}
                    </span>
                    <span className="hidden lg:inline">Profile</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span>Sign In</span>
                  </>
                )}
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            type="button"
            className="md:hidden p-2 rounded text-archive-text hover:text-archive-gold hover:bg-archive-stone/50 transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-expanded={isMenuOpen}
            aria-label="Toggle navigation menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {isMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-archive-stone/30">
            <div className="flex flex-col gap-1">
              {/* Mobile Search Link */}
              <NavLink
                to="/search"
                onClick={() => setIsMenuOpen(false)}
                className={({ isActive }) =>
                  `px-4 py-3 text-sm font-medium tracking-wide transition-colors duration-200 rounded flex items-center gap-2 ${
                    isActive
                      ? 'text-archive-gold bg-archive-gold/10'
                      : 'text-archive-text hover:text-archive-gold hover:bg-archive-stone/50'
                  }`
                }
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Search
              </NavLink>

              {navItems.map((item) =>
                item.path.startsWith('http') ? (
                  // External flagship item — subtle gold accent to read as the product.
                  <a
                    key={item.path}
                    href={item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsMenuOpen(false)}
                    className="px-4 py-3 text-sm font-medium tracking-wide transition-colors duration-200 rounded text-archive-gold hover:bg-archive-gold/10"
                  >
                    {item.label}
                  </a>
                ) : (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={({ isActive }) =>
                      `px-4 py-3 text-sm font-medium tracking-wide transition-colors duration-200 rounded ${
                        isActive
                          ? 'text-archive-gold bg-archive-gold/10'
                          : 'text-archive-text hover:text-archive-gold hover:bg-archive-stone/50'
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                )
              )}

              {/* Mobile Auth Link */}
              {!loading && (
                <Link
                  to={user ? '/profile' : '/login'}
                  onClick={() => setIsMenuOpen(false)}
                  className="px-4 py-3 text-sm font-medium tracking-wide transition-colors duration-200 rounded text-archive-gold hover:bg-archive-gold/10 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  {user ? 'My Profile' : 'Sign In'}
                </Link>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  )
}
